// Third-party
import React, { useEffect } from 'react';
import { RouteComponentProps, Route, Redirect, Switch, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

// Components
import AdminEnterprise from './enterprise/AdminEnterprise';
import AdminDataPortal from './admin-portal/AdminDataPortal';
import AdminGroups from './group/AdminGroups';
import PageLayout from '../layouts/PageLayout';
import { ErrorBoundary } from '@/views/components/errors';
import { SecondaryNavigation } from '@/views/components/navigation';

// Utils
import { formatMessage } from '@/utils/i18n';
import { getAdminUserEnterpriseDetails } from '@/utils/admin';
import { PERMISSIONS, userHasPermissionEnabled, userHasPermission } from '@/utils/permissions';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { getEnterpriseAdminInfo, saveEnterpriseInfos } from '@/redux/modules/enterprise';
import { addEnterpriseGroup } from '@/redux/modules/enterprise/admin/groups';
import { selectDelegateMode } from '@/redux/modules/auth/selectors';
import { setPortfolioEnterprise } from '@/redux/modules/edit-portfolio';

// Types
import AppRegion from '@/types/app-region';
import EnterpriseAdminDetails from '@/types/enterprise-admin-details';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import EnterpriseInfos from '@/types/enterprise-infos';
import EnterpriseGroup from '@/types/enterprise-group';
import User from '@/types/user';

// TODO: @deprecate when we deprecate Legacy
import LegacyAdmin from './LegacyAdmin';
import useThemeVersion from '@/hooks/useThemeVersion';

enum AdminTabs {
  DATA_PORTAL = '/admin/data-upload',
  ENTERPRISE = '/admin/enterprise',
  GROUPS = '/admin/groups',
}

type StateProps = {
  adminDetails: EnterpriseAdminDetails;
  delegateMode: boolean;
  enterprise: EnterpriseDetails;
  user: User;
};

type DispatchProps = {
  addEnterpriseGroup: (enterpriseId: string, name: string) => Promise<EnterpriseGroup>;
  getEnterpriseAdminInfo: (id: string) => Promise<EnterpriseAdminDetails>;
  saveEnterpriseInfos: (id: string, infos: EnterpriseInfos) => Promise<EnterpriseInfos>;
  setPortfolioEnterprise: () => void;
};

type Props = RouteComponentProps & StateProps & DispatchProps;

const Admin: React.FC<Props> = (props: Props) => {
  const { adminDetails, delegateMode, enterprise, match, user, addEnterpriseGroup, getEnterpriseAdminInfo, saveEnterpriseInfos, setPortfolioEnterprise } =
    props;

  const isAdminPortalAccess = userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_DATA_UPLOAD);

  const getPillsFilterElementItems = () => [
    {
      value: AdminTabs.ENTERPRISE,
      label: formatMessage('Admin.Nav.Enterprise'),
      path: `${match.path}/enterprise`,
      hidden:
        !userHasPermissionEnabled(
          user,
          PERMISSIONS.ENTERPRISE_ADMIN,
          PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MODELS,
          PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
        ) ||
        delegateMode ||
        adminDetails == null ||
        enterprise == null,
    },
    {
      value: AdminTabs.GROUPS,
      label: formatMessage('Admin.Nav.Groups'),
      hidden:
        !userHasPermissionEnabled(
          user,
          PERMISSIONS.ENTERPRISE_ADMIN_GROUPS,
          PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MODELS,
          PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
        ) ||
        delegateMode ||
        adminDetails == null ||
        enterprise == null,
      path: `${match.path}/groups`,
    },
    {
      value: AdminTabs.DATA_PORTAL,
      label: formatMessage('Admin.Nav.DataUpload'),
      path: `${match.path}/data-upload`,
      hidden: !isAdminPortalAccess || delegateMode,
    },
  ];

  const getAdminRoutes = () => {
    const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    const routeDetails = getPillsFilterElementItems();

    return [
      {
        ...routeDetails[0],
        render: (props: RouteComponentProps) => (
          <AdminEnterprise {...props} appRegion={appRegion} enterprise={enterprise} pills={routeDetails} user={user} onSave={onSaveEnterpriseInfos} />
        ),
      },
      {
        ...routeDetails[1],
        render: (props: RouteComponentProps) => (
          <AdminGroups
            {...props}
            adminDetails={adminDetails}
            groups={adminDetails?.groups || []}
            pills={routeDetails}
            user={user}
            onAddGroup={onAddGroupToEnterprise}
          />
        ),
      },
      {
        ...routeDetails[2],
        render: () => (
          <PageLayout
            pageTitle="Admin.PageTitle"
            mainClassName="admin-v2"
            pillsFilterElement={<SecondaryNavigation className="navigation-pills" items={routes} />}
          >
            <div className="content">
              <AdminDataPortal />
            </div>
          </PageLayout>
        ),
      },
    ].filter(route => !route.hidden);
  };

  const onAddGroupToEnterprise = (name: string): Promise<EnterpriseGroup> => {
    if (!enterprise) {
      return Promise.reject();
    }

    // Reload enterprise's data after adding a new group
    return addEnterpriseGroup(enterprise._id, name).then(newGroup => newGroup);
  };

  // Enterprise Admin Actions
  const onSaveEnterpriseInfos = (infos: EnterpriseInfos) => {
    if (!enterprise) {
      return Promise.reject();
    }
    return saveEnterpriseInfos(enterprise._id, infos);
  };

  useEffect(() => {
    const userEnterprise = getAdminUserEnterpriseDetails(user);

    if (userEnterprise) {
      getEnterpriseAdminInfo(userEnterprise._id);
      setPortfolioEnterprise();
    }
  }, [user, getEnterpriseAdminInfo, setPortfolioEnterprise]);

  const routes = getAdminRoutes();

  return (
    <ErrorBoundary>
      <Switch>
        {routes.map(route => (
          <Route key={route.value} path={route.path} render={route.render} />
        ))}
        <Route exact path={`${match.path}/`} render={() => <Redirect to={routes.length > 0 ? routes[0].path : '/admin'} />} />
      </Switch>
    </ErrorBoundary>
  );
};

const AdminComponent = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <Admin {...props} />;
  }
  return <LegacyAdmin {...props} />;
};

const mapStateToProps = (state: ReduxState) => ({
  adminDetails: state.enterprise.admin,
  delegateMode: selectDelegateMode(state),
  enterprise: state.enterprise.details,
});

export default withRouter(
  connect(mapStateToProps, {
    addEnterpriseGroup,
    getEnterpriseAdminInfo,
    saveEnterpriseInfos,
    setPortfolioEnterprise,
  })(AdminComponent),
);
